<script lang="ts">
    import { COUNTRIES } from "@/constants/countryInfo";
    import { DATES } from "@/constants/dates";
    import ReviewsTextInput from "@apps/reviews/components/ReviewsTextInput/ReviewsTextInput.svelte";
    import { ATTRIBUTE_TYPE } from "@apps/reviews/constants";
    import AttributesEntity from "@apps/reviews/entities/AttributesEntity";
    import FormManager from "@lib/FormManager/FormManager";
    import FormManagerController from "@lib/FormManager/FormManagerController.svelte";
    import FormManagerProvider from "@lib/FormManager/FormManagerProvider.svelte";
    import { FormManagerErrors } from "@lib/FormManager/types";

    import FormLayout from "@components/FormLayout/FormLayout.svelte";
    import FormLayoutItem from "@components/FormLayout/FormLayoutItem.svelte";
    import FormMultipleChooseField from "@components/forms/FormChooseField/FormMultipleChooseField.svelte";
    import FormSingleChooseField from "@components/forms/FormSingleChooseField/FormSingleChooseField.svelte";
    import Range from "@components/forms/Range.svelte";
    import Dropdown from "@components/forms/Select/Dropdown.svelte";
    import SelectDate from "@components/forms/Select/SelectDate.svelte";

    export let formManager: FormManager;
    export let attributes: AttributesEntity;
    export let errors: FormManagerErrors;
    export let namespace: string;
    let attributesRef: HTMLDivElement;

    $: {
        errors;
        const errorsKeys = Object.keys(errors);

        if (errorsKeys.length && attributesRef) {
            const firstKey = errorsKeys[0];
            const attributesBlock = attributesRef.querySelector(
                `#gw-attributes-${firstKey}`
            );

            if (attributesBlock) {
                attributesBlock.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }
        }
    }
</script>

<FormManagerProvider {formManager}>
    <div class="gw-rv-attributes" bind:this={attributesRef}>
        <FormLayout>
            {#each [...attributes.customerAttributes, ...attributes.productAttributes] as attribute (attribute.id)}
                <div class="gw-rv-attributes__list">
                    <FormLayoutItem
                        label={attribute.title}
                        helpText={""}
                        errors={errors[attribute.id] || []}
                        gap="8px"
                        required={attribute.isRequired}
                        id={`gw-attributes-${attribute.id}`}
                        {namespace}
                    >
                        {#if attribute.content.type === ATTRIBUTE_TYPE.SINGLE_VALUE}
                            {#if attribute.content.asDropdown}
                                <FormManagerController
                                    let:field
                                    name={attribute.id}
                                >
                                    <Dropdown
                                        placeholderText={"Select answer"}
                                        items={attribute.content.options?.map(
                                            (option) => ({
                                                label: option,
                                                value: option,
                                            })
                                        ) || []}
                                        value={field.value}
                                        on:change={field.onChange}
                                        inputAttributes={{
                                            name: `$select-${attribute.id}`,
                                        }}
                                    />
                                </FormManagerController>
                            {:else}
                                <FormManagerController
                                    name={attribute.id}
                                    let:field
                                >
                                    <FormSingleChooseField
                                        options={attribute.content.options.map(
                                            (opt, i) => ({
                                                label: opt,
                                                value: opt,
                                                disabled: false,
                                            })
                                        ) || []}
                                        value={field.value}
                                        name={String(attribute.id)}
                                        on:change={field.onChange}
                                    />
                                </FormManagerController>
                            {/if}
                        {/if}

                        {#if attribute.content.type === ATTRIBUTE_TYPE.MULTI_VALUE}
                            <FormManagerController
                                name={attribute.id}
                                let:field
                            >
                                <FormMultipleChooseField
                                    options={attribute.content.options?.map(
                                        (option) => ({
                                            label: option,
                                            value: option,
                                        })
                                    ) || []}
                                    on:change={field.onChange}
                                    value={field.value}
                                />
                            </FormManagerController>
                        {/if}

                        {#if attribute.content.type === ATTRIBUTE_TYPE.TEXTFIELD}
                            <FormManagerController
                                let:field
                                name={attribute.id}
                            >
                                <ReviewsTextInput
                                    aria-label={attribute.title}
                                    type="text"
                                    placeholder={attribute.content
                                        .placeholder || ""}
                                    value={field.value}
                                    on:change={field.onChange}
                                />
                            </FormManagerController>
                        {/if}

                        {#if attribute.content.type === ATTRIBUTE_TYPE.RANGE}
                            <FormManagerController
                                name={attribute.id}
                                let:field
                            >
                                <Range
                                    items={attribute.content.options || []}
                                    name={attribute.id}
                                    value={field.value}
                                    minLabel={attribute.content.minLabel}
                                    maxLabel={attribute.content.maxLabel}
                                    isRange
                                    on:change={field.onChange}
                                />
                            </FormManagerController>
                        {/if}

                        {#if attribute.content.type === ATTRIBUTE_TYPE.CENTERED_RANGE}
                            <FormManagerController
                                name={attribute.id}
                                let:field
                            >
                                <Range
                                    items={attribute.content.options || []}
                                    name={attribute.id}
                                    value={field.value}
                                    minLabel={attribute.content.minLabel}
                                    midLabel={attribute.content.midLabel}
                                    maxLabel={attribute.content.maxLabel}
                                    on:change={field.onChange}
                                />
                            </FormManagerController>
                        {/if}

                        {#if attribute.content.type === ATTRIBUTE_TYPE.DATE}
                            {@const component =
                                attribute.content.component.split("_")}

                            <FormManagerController
                                name={attribute.id}
                                let:field
                            >
                                <SelectDate
                                    {component}
                                    dayItems={[...Array(31)].map((_, i) => ({
                                        label: `${i + 1}`,
                                        value: i + 1,
                                    }))}
                                    monthItems={DATES}
                                    yearItems={[...Array(101)].map((_, i) => ({
                                        label: `${
                                            new Date().getFullYear() - i
                                        }`,
                                        value: new Date().getFullYear() - i,
                                    }))}
                                    value={field.value}
                                    on:change={field.onChange}
                                />
                            </FormManagerController>
                        {/if}

                        {#if attribute.content.type === ATTRIBUTE_TYPE.LOCATION}
                            <FormManagerController
                                name={attribute.id}
                                let:field
                            >
                                <Dropdown
                                    searchable
                                    placeholderText={"Select country"}
                                    items={COUNTRIES.map((c) => {
                                        return {
                                            label: c.value,
                                            value: c.code,
                                        };
                                    })}
                                    value={field.value}
                                    on:change={field.onChange}
                                    inputAttributes={{
                                        name: `$select-${attribute.id}`,
                                    }}
                                />
                            </FormManagerController>
                        {/if}
                    </FormLayoutItem>
                </div>
            {/each}
        </FormLayout>
    </div>
</FormManagerProvider>

<style lang="postcss">
    .gw-rv-attributes {
        display: contents;

        &__list {
            padding: 1em 0 0;
        }
    }
</style>
